import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import type { LimaSourcesResponseModal } from '@/features/DevicesTable';
import { filteredLimaSourcesAtom } from '@/pages/Administration/Devices/Devices.store';

import SOURCE_TYPE from '../SourceCreate.constants';
import { useSourceCreateState, useSourceDialog } from '../SourceCreate.hooks';
import { SourceDialogMode } from '../SourceCreate.types';

// --- Helper Functions ---

/**
 * Checks if Dropbox OAuth credentials should be disabled.
 *
 * Business Rule: Once Dropbox is configured, OAuth credentials cannot be changed
 * to prevent breaking the existing authorization flow.
 */
const isDropboxOAuthField = (fieldName: string): boolean =>
  ['client_key', 'client_secret', 'authorize_url'].includes(fieldName);

/**
 * Checks if source ingestion has never started (safe to modify ingest_from date).
 *
 * Business Rule: A source is considered "never started" if:
 * - No status records exist, OR
 * - Status array is empty, OR
 * - First status has no process details, OR
 * - First status is disabled (1900) with zero start/end times
 */
const hasSourceNeverStarted = (
  limaSource: LimaSourcesResponseModal | undefined
): boolean => {
  if (!limaSource) return true;

  const { source_statuses } = limaSource;

  // No statuses recorded
  if (source_statuses === null) return true;

  // Empty status array
  if (Array.isArray(source_statuses) && source_statuses.length === 0)
    return true;

  const firstStatus = source_statuses?.[0];

  // No process details
  if (!firstStatus?.process_details) return true;

  // Disabled status with zero processing time
  const { process_start_time, process_end_time } = firstStatus.process_details;
  const isDisabledStatus = firstStatus.status_code === 1900;
  const hasNeverProcessed = process_start_time === 0 && process_end_time === 0;

  return isDisabledStatus && hasNeverProcessed;
};

/**
 * Determines if the ingest_from field should be editable.
 *
 * Business Rule: Allow editing ingest_from if:
 * 1. Override flag is set (post-migration cleanup), OR
 * 2. Source is inactive AND has never started ingestion
 */
const canEditIngestFrom = (
  isEditMode: boolean,
  overrideFlag: boolean,
  sourceIsActive: boolean,
  limaSource: LimaSourcesResponseModal | undefined
): boolean => {
  if (!isEditMode) return false;
  if (overrideFlag) return true;

  return !sourceIsActive && hasSourceNeverStarted(limaSource);
};

// --- Main Hook ---

/**
 * Applies source-specific validation rules and field state modifications.
 *
 * This hook handles:
 * - Default value population in New mode
 * - Dropbox: Prevents OAuth credential changes after initial setup
 * - CrowdStrike: Locks ingest_data_from to prevent historical data gaps
 * - All sources: Conditionally enables ingest_from based on ingestion state
 */
const useCustomValidatons = () => {
  const { setValue } = useFormContext();
  const {
    selectedSourceDetails,
    setConnectionInputSchema,
    connectionInputSchema
  } = useSourceCreateState();
  const { mode } = useSourceDialog();
  const [filteredLimaSources] = useAtom(filteredLimaSourcesAtom);

  const isEditMode = mode === SourceDialogMode.Edit;
  const sourceTypeId = Number(selectedSourceDetails.source_type_id);

  // Apply default values for new sources
  useEffect(() => {
    if (mode === SourceDialogMode.New && connectionInputSchema?.source_inputs) {
      connectionInputSchema.source_inputs.forEach((field) => {
        if (field?.defaultValue) {
          setValue(field.name, field.defaultValue, {
            shouldDirty: false,
            shouldTouch: false
          });
        }
      });
    }
  }, [mode, connectionInputSchema, setValue]);

  // Apply source-specific field modifications (Dropbox, CrowdStrike, ingest_from)
  useEffect(() => {
    if (!connectionInputSchema?.source_inputs) return;

    let needsUpdate = false;
    const updatedInputs = [...connectionInputSchema.source_inputs];

    /**
     * Dropbox: Lock OAuth credentials after initial configuration
     *
     * Business Context: Changing OAuth credentials would invalidate the existing
     * authorization token, breaking the source's access to Dropbox data.
     */
    if (isEditMode && sourceTypeId === SOURCE_TYPE.DROPBOX) {
      const existingAccessCode =
        selectedSourceDetails?.source_inputs?.find(
          (input) => input.name === 'access_code'
        )?.value || '';

      updatedInputs.forEach((field) => {
        if (isDropboxOAuthField(field.name) && !field.disabled) {
          field.disabled = true;
          needsUpdate = true;
        }

        // Lock access_code if already generated
        if (
          field.name === 'access_code' &&
          existingAccessCode.length > 0 &&
          !field.disabled
        ) {
          field.disabled = true;
          needsUpdate = true;
        }
      });
    }

    /**
     * CrowdStrike: Lock ingest_data_from date
     *
     * Business Context: Changing the ingestion start date could create gaps
     * in the historical data timeline or cause duplicate data ingestion.
     */
    if (isEditMode && sourceTypeId === SOURCE_TYPE.CROWD_STRIKE) {
      updatedInputs.forEach((field) => {
        if (field.name === 'ingest_data_from' && !field.disabled) {
          field.disabled = true;
          needsUpdate = true;
        }
      });
    }

    /**
     * All sources: Conditionally enable ingest_from field
     *
     * Business Context: After Lima sources migration, some sources need a one-time
     * ingest_from update. Otherwise, only allow editing if the source has never
     * started ingestion to prevent data timeline issues.
     */
    const overrideFlag =
      selectedSourceDetails?.enable_ingest_from_on_edit || false;
    const sourceIsActive = selectedSourceDetails?.active !== false;
    const limaSource = filteredLimaSources.find(
      (source: LimaSourcesResponseModal) =>
        source.source_uid === selectedSourceDetails?.source_uid
    );

    const shouldEnableIngestFrom = canEditIngestFrom(
      isEditMode,
      overrideFlag,
      sourceIsActive,
      limaSource
    );

    updatedInputs.forEach((field) => {
      if (field.name === 'ingest_from') {
        const newDisabledState = !shouldEnableIngestFrom;
        if (field.disabled !== newDisabledState) {
          field.disabled = newDisabledState;
          needsUpdate = true;
        }
      }
    });

    // Only update state if something actually changed
    if (needsUpdate) {
      setConnectionInputSchema({
        ...connectionInputSchema,
        source_inputs: updatedInputs
      });
    }
  }, [
    connectionInputSchema,
    isEditMode,
    sourceTypeId,
    selectedSourceDetails,
    filteredLimaSources,
    setConnectionInputSchema
  ]);

  return {};
};
export default useCustomValidatons;
